import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Square, Timer } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";

/**
 * ActiveTimerPill — fixed bottom-right pill visible any time the current
 * user has a running boards timer. Polls once on mount and reconciles on
 * SSE (`timer.started` / `timer.stopped`). Click title → jump to card in
 * the board drawer. Click stop → POST timer/stop.
 *
 * Rendered only when the caller has the boards module enabled + view cap.
 */
export default function ActiveTimerPill() {
  const nav = useNavigate();
  const { companyId } = useParams();
  const { entitlements, has } = useCompany();
  const moduleOn = !!entitlements?.modules?.boards;
  const canView = has("boards.view");

  const [active, setActive] = React.useState(null);
  const [card, setCard] = React.useState(null);
  const [tick, setTick] = React.useState(0);

  const enabled = !!companyId && moduleOn && canView;

  const load = React.useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/boards/timer/active`);
      setActive(data.active || null);
      setCard(data.card || null);
    } catch {
      // silent — the pill just won't render
    }
  }, [companyId, enabled]);

  React.useEffect(() => { load(); }, [load]);

  // 1s tick so elapsed keeps counting
  React.useEffect(() => {
    if (!active) return () => {};
    const h = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(h);
  }, [active]);

  // Live: react to timer events for this user
  React.useEffect(() => {
    const on = (e) => {
      const evt = e.detail;
      if (evt?.module !== "boards") return;
      if (["timer.started", "timer.stopped"].includes(evt.type)) load();
    };
    window.addEventListener("fable:event", on);
    return () => window.removeEventListener("fable:event", on);
  }, [load]);

  if (!enabled || !active) return null;

  const startedMs = new Date(active.started_at).getTime();
  const nowMs = Date.now();
  const totalSec = Math.max(0, Math.floor((nowMs - startedMs) / 1000));
  const hh = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  const elapsed = hh === "00" ? `${mm}:${ss}` : `${hh}:${mm}:${ss}`;

  async function stop() {
    try {
      await api.post(`/companies/${companyId}/boards/${active.board_id}/cards/${active.card_id}/timer/stop`);
      setActive(null);
      setCard(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not stop timer");
    }
  }

  function jump() {
    nav(`/c/${companyId}/m/boards/${active.board_id}?card=${active.card_id}`);
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 pl-3 pr-1.5 py-1 rounded-full bg-brand-ink text-white shadow-card"
      data-testid="active-timer-pill"
      data-tick={tick}
    >
      <span className="h-2 w-2 rounded-full bg-brand-violet-hi animate-pulse" />
      <Timer size={12} className="text-white/70" />
      <button
        type="button"
        onClick={jump}
        className="text-[12px] font-medium max-w-[220px] truncate hover:underline"
        data-testid="active-timer-pill-jump"
        title={card?.title || "Open card"}
      >
        {card?.title || "Timer running"}
      </button>
      <span
        className="mono-num text-[11.5px] tabular-nums bg-card/10 px-2 py-0.5 rounded-full"
        data-testid="active-timer-pill-elapsed"
      >
        {elapsed}
      </span>
      <button
        type="button"
        onClick={stop}
        className="h-7 w-7 rounded-full bg-brand-coral hover:bg-red-500 flex items-center justify-center transition-colors"
        data-testid="active-timer-pill-stop"
        title="Stop timer"
        aria-label="Stop timer"
      >
        <Square size={12} className="fill-white text-white" />
      </button>
    </div>
  );
}
